import React, {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';

import * as Sentry from '@sentry/nextjs';
import config from 'config';
import PropTypes from 'prop-types';
import { BsArrowsAngleExpand } from 'react-icons/bs';
import { GrPlay } from 'react-icons/gr';
import { useSelector } from 'react-redux';
import Sticky from 'react-stickynode';
import ResizeObserver from 'resize-observer-polyfill';
import styled, { css } from 'styled-components';

import { useCustomer } from '@techstyle/react-accounts';
import { Video, useBreakpoint } from '@techstyle/react-components';
import { useFeatures } from '@techstyle/react-features';
import { FormattedMessage } from '@techstyle/react-intl';
import { useDomain, useLocalStorage, useSession } from '@techstyle/redux-core';

import generateCdnImageUrl from '../../../src/utils/generateCdnImageUrl';
import { AddToBagErrorCode } from '../../constants/checkout';
import { prepModelData } from '../../segment-events/utils';
import { mobile, desktop } from '../../styles';
import fetchVideoThumbnail from '../../utils/fetchVideoThumbnail';
import formatVimeoThumbnailUrl from '../../utils/formatVimeoThumbnailUrl';
import isNewPricingPromoBannersFlagOn from '../../utils/isNewPricingPromoBannersFlagOn';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { usePdpSingleCTAFlag } from '../../utils/LD/usePdpSingleCTAFlag';
import {
  getPriceDetails,
  isXtraVipBox as isXtraVipBoxSelector,
  isSoldOut as isSoldOutSelector,
} from '../../utils/selectors';
import { isOneSize } from '../../utils/sizes';
import { useMembership } from '../../utils/useMembership';
import useOrderedModels from '../../utils/useOrderedModels';
import useUnlockedXclusive from '../../utils/useUnlockedXclusive';
import { useByoContext } from '../ByoProvider';
import { Component as PairItWithRecommendations } from '../PairItWithRecommendations';
import { Component as ProductDetailConfidence } from '../ProductDetailConfidence';
import { Component as ProductDetailCustomizationToggle } from '../ProductDetailCustomizationToggle';
import { Component as ProductDetailDescription } from '../ProductDetailDescription';
import { Component as ProductDetailFlag } from '../ProductDetailFlag';
import { Container as ProductDetailHeader } from '../ProductDetailHeader';
import { Component as ProductDetailModelThumbnails } from '../ProductDetailModelThumbnails';
import { Component as ProductDetailName } from '../ProductDetailName';
import { Component as ProductDetailRecommendations } from '../ProductDetailRecommendations';
import { Container as ProductDetailReviews } from '../ProductDetailReviews';
import { Component as ProductDetailSizeLinks } from '../ProductDetailSizeLinks';
import { Container as ProductDetailStars } from '../ProductDetailStars';
import { Component as ProductImageCarousel } from '../ProductImageCarousel';
import { Container as ProductImpression } from '../ProductImpression';
import { Component as ProductRecommendations } from '../ProductRecommendations';
import { Component as ProductSetDetailOptions } from '../ProductSetDetailOptions';
import { Component as ProductSingleDetailOptions } from '../ProductSingleDetailOptions';
import { VideoCoverSizes } from '../ProductVideo/ProductVideo';
import VipLearnMoreLink from '../VipLearnMoreLink/VipLearnMoreLinkContainer';
import { Component as VIPLearnMoreModal } from '../VIPLearnMoreModal';
import { Component as WishlistButton } from '../WishlistButton';

import { ProductDetailContext } from './ProductDetailContext';

const ARROW_OFFSET = 10;

// TODO: "one size" items don't get properly labeled as missy/curvy in
// product_id_object_list on the backend so we have to do it here. This
// should be handled by API or ES (please delete this hook when TRO/ES
// work is done).
const curvySizeTag = config.get('public.productTags.plusSize');
const setId = config.get('public.productTypes.setId');
const isModelEnabled = config.get('public.showAltModels');
const useReorderOneSizeLinkedProducts = product => {
  return useMemo(() => {
    function fixOneSizeSettings(product) {
      const mapCorrectPlusSize = (parentProduct, item) => {
        const isCurvy = parentProduct.tag_id_list.includes(curvySizeTag);
        return {
          ...item,
          is_plus_size: item.is_self ? isCurvy : !isCurvy,
        };
      };

      let {
        product_id_object_list: productIdObjectList,
        related_product_id_object_list: relatedProductIdObjectList,
      } = product;
      const isOneSizeItem = isOneSize(product);

      // We can safely assume that if it's "one size" and has multiple items,
      // the other items are also "one size"
      if (isOneSizeItem && productIdObjectList.length > 1) {
        productIdObjectList = productIdObjectList
          .map(item => mapCorrectPlusSize(product, item))
          .sort(a => (a.is_plus_size ? 0 : -1));

        relatedProductIdObjectList = relatedProductIdObjectList.map(
          related => ({
            ...related,
            product_id_object_list: related.product_id_object_list
              .map(item => mapCorrectPlusSize(related, item))
              .sort(a => (a.is_plus_size ? 0 : -1)),
          })
        );
      }

      return {
        ...product,
        product_id_object_list: productIdObjectList,
        related_product_id_object_list: relatedProductIdObjectList,
      };
    }

    const isSet = product.product_type_id === setId;
    if (isSet) {
      return {
        ...product,
        products_in_set: product.products_in_set.map(p =>
          fixOneSizeSettings(p)
        ),
      };
    } else {
      return fixOneSizeSettings(product);
    }
  }, [product]);
};

const Row = styled.div.attrs(props => {
  return {
    className: `${props.className || ''} bfx-product bfx-price-product`.trim(),
  };
})`
  ${mobile`
    flex-direction: column;
  `};

  @media (min-width: ${({ theme }) =>
      theme.breakpoints.large}px) and (max-width: ${({ theme }) =>
      theme.breakpoints.xxlarge}px) {
    padding-bottom: ${({ theme }) => theme.spacing.large}px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xxlarge}px) {
    padding-bottom: 56px;
  }

  display: flex;
  flex-wrap: nowrap;
  margin: 0 auto;
  max-width: 1024px;
  width: 100%;
`;

const LeftColumn = styled.div`
  ${mobile`
    max-width: 100vw;
    margin-left: 0;
    padding-top: 12px;
  `};

  ${desktop`
    flex-basis: 55%;
    margin-left: 24px;
    max-width: 55%;
  `};

  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  order: 1;

  @media (min-width: ${props => props.theme.breakpoints.xlarge}px) {
    margin-left: 66px;
  }

  @media (min-width: ${props => props.theme.breakpoints.xxlarge}px) {
    flex-basis: 64%;
    margin-left: 40px;
    max-width: 64%;
  }

  @media (min-width: ${props => props.theme.breakpoints.xlarge}px),
    (min-width: ${props => props.theme.breakpoints.xxlarge}px) {
    flex-basis: 58%;
    margin-left: 24px;
    max-width: 58%;
  }
`;

/* 1. When the ThumbnailCarousel changes width, the Main Image Carousel overflows for a second.
 * This hides it.
 */
const CarouselWrapper = styled.div`
  max-height: 800px; /* 1 */
  max-width: 816px;
  overflow: hidden; /* 1 */
  position: relative;

  @media (min-width: 1201px) {
    max-width: 686px;
  }
  @media (min-width: 1301px) {
    max-width: 726px;
  }
`;

const StyledProductImageCarousel = styled(ProductImageCarousel)`
  .slick-dots {
    padding-bottom: ${props => (props.hasTaggStarElement ? '36px' : '0')};
    transition: padding-bottom 0.2s ease-in-out;
  }
`;

const RightColumn = styled.div`
  ${mobile`
    margin-right: 0;
    padding-left: 0;
    padding-bottom: ${({ theme }) => theme.spacing.tiny}px;
  `};

  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 30%;
  margin-right: ${props => props.theme.spacing.small}px;
  padding-left: ${props => props.theme.spacing.moderate}px;
  padding-bottom: ${props => props.theme.spacing.moderate}px;
  min-width: 292px;
  order: 2;

  @media (min-width: ${props => props.theme.breakpoints.xlarge}px) {
    margin-right: 0;
    padding-left: 40px;
  }
`;

const Details = styled.div`
  ${mobile`
    padding-left: 0px;
    padding-right: 0px;
    padding-top: 0px;
  `}
  padding-top: ${ARROW_OFFSET + 2}px;
`;

const StyledProductDetailFlag = styled(ProductDetailFlag)`
  align-self: flex-end;
  padding-top: ${props => props.theme.spacing.tiny}px;
  ${desktop`
    display: none;
  `};
`;

const Reviews = styled.div`
  margin: 0 auto;
  ${desktop`
    scroll-margin-top: 100px;
  `}
  ${mobile`
    scroll-margin-top: 50px;
  `}
`;

const StyledProductDetailReviews = styled(ProductDetailReviews)`
  @media (min-width: ${({ theme }) =>
      theme.breakpoints.small}px) and (max-width: ${({ theme }) =>
      theme.breakpoints.xxlarge}px) {
    padding: ${({ theme }) => theme.spacing.large}px 30px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xxlarge}px) {
    padding-bottom: 56px;
    padding-top: 56px;
  }

  ${ProductDetailReviews.ReviewCount} {
    ${mobile`margin-top: 0;`}
  }

  ${mobile`
    padding: 0 16px;
  `}
`;

const ProductDetailDescriptionWrapper = styled.div`
  padding-top: ${({ theme }) => theme.spacing.moderate}px;

  ${mobile`padding-top: ${({ theme }) => theme.spacing.large}px;`}
`;

const StyledProductDetailStars = styled(ProductDetailStars)`
  ${props => props.theme.subHeader.variants.subHeading2.textStyles}
  align-self: flex-start;
  flex: 1;
  margin: ${props => props.theme.spacing.small}px
    ${props => props.theme.spacing.small}px 0 0;
  ${desktop`
    text-align: left;
  `};
`;
const SetWrapper = styled.div`
  ${mobile`
    padding-left: ${props => props.theme.spacing.small}px;
    padding-right: ${props => props.theme.spacing.small}px;
  `}
`;
const ProductSetDetailSizeLinks = styled(ProductDetailSizeLinks)`
  > ${ProductDetailSizeLinks.SizeGuideButton} {
    margin-left: 0px;
  }
`;

const MobileWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  padding: 0 ${props => props.theme.spacing.small}px
    ${props => props.theme.spacing.small}px;
`;

const WrapperButtons = styled.div`
  position: absolute;
  right: ${props => props.theme.spacing.small}px;
  z-index: ${props => props.theme.zIndex.productImageCarousel};
  top: ${props => props.theme.spacing.small}px;
  display: flex;
  flex-direction: row;
  gap: ${props => props.theme.spacing.tiny}px;
`;

const ExpandIcon = styled.div`
  background-color: ${props => props.theme.colors.whiteSubdued};
  top: ${props => props.theme.spacing.small}px;
  right: ${props => props.theme.spacing.small}px;
  line-height: 0;
  padding: ${props => props.theme.spacing.tiny}px;
  z-index: ${props => props.theme.zIndex.productImageCarousel};
  cursor: pointer;
`;

const StyledWishlistButton = styled(WishlistButton)`
  height: 20px;
  width: 20px;
  margin: 10px;

  > svg {
    margin-bottom: 2px;
  }
`;

const MobileHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  width: 100%;

  ${mobile`

    ${ProductDetailName.ProductNameContainer} {
      flex: 0 1 auto;
    }

    ${StyledWishlistButton} {
      margin: 4px;
      > svg {
        margin-bottom: 4px;
      }
    }
  `}
`;

const ProductDetailPlayButton = styled.button`
  ${props => props.theme.paragraph.variants.paragraph5Uppercase.textStyles}
  letter-spacing: 0;
  background-color: ${props => props.theme.colors.whiteSubdued};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.tiny / 2}px;
  height: auto;
  padding: ${props => props.theme.spacing.tiny + 2}px;
  cursor: pointer;

  > svg {
    margin-bottom: 0;
    margin-left: 4px;
    path {
      stroke: ${props => props.theme.colors.black};
    }
  }
`;

const ProductDetailSizeIncludeLabel = styled.div`
  margin-top: ${props => props.theme.spacing.moderate}px;
`;

const SuggestedProductContainer = styled.div`
  ${props =>
    props.hasDarkBackground &&
    css`
      background-color: ${props => props.theme.colors.gunmetal};
    `}

  @media (min-width: ${({ theme }) =>
    theme.breakpoints.small}px) and (max-width: ${({ theme }) =>
    theme.breakpoints.xxlarge}px) {
    padding: ${({ theme }) => theme.spacing.large}px 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xxlarge}px) {
    padding: 56px 0;
  }

  &:empty {
    border-top: none;
    padding: 0;
  }
`;

const StyledProductDetailRecommendations = styled(ProductDetailRecommendations)`
  max-width: ${props => props.theme.breakpoints.xlarge}px;
  margin: 0px auto;
  padding: 0px ${props => props.theme.spacing.small}px 0px;

  ${ProductRecommendations.Title} {
    ${props =>
      props.variantStyle &&
      css`
        margin-top: 0;
      `}
  }

  ${ProductRecommendations.ProductList} {
    padding-bottom: 0;

    .slick-slider {
      padding-bottom: 0;
    }
  }
`;

const StyledPairItWithRecommendations = styled(PairItWithRecommendations)`
  max-width: ${props => props.theme.breakpoints.xlarge}px;
  margin: 0px auto;
  padding: 0px ${props => props.theme.breakpoints.small}px 0px;

  @media (max-width: ${props => props.theme.breakpoints.medium}px) {
    padding-right: 0;
  }

  ${ProductRecommendations.Title} {
    ${props =>
      props.variantStyle &&
      css`
        margin-top: 0;
      `}
  }

  ${ProductRecommendations.CurrentItemWrapper} {
    ${desktop`
      margin-top: ${props => props.theme.spacing.small}px;
    `}
  }

  ${ProductRecommendations.ProductList} {
    padding-bottom: 0;

    .slick-slider {
      padding-bottom: 0;
    }
  }
`;

const RecommendedContainer = styled.div`
  width: 100%;

  ${mobile`margin: 0px;`}
`;

const StyledSticky = styled(Sticky)`
  & > .sticky-inner-wrapper {
    z-index: 2;
    background-color: ${props => props.theme.colors.white};
    padding-top: 12px;
  }
`;

const StickyHeaderSet = styled(StyledSticky)`
  & > .sticky-inner-wrapper {
    z-index: ${({ enabled }) => (enabled ? 3 : 2)};
  }
`;

const StickyLeftColumnContent = styled(Sticky)``;

const ProductSetDetailWrapper = styled.div``;

const CustomizationToggleWrapper = styled.div``;

const defaultImages = [];

export default function ProductDetailLayout({
  className,
  handleModelImageFullScreen,
  hasMembershipItem,
  isCurvy,
  isExclusive,
  isLinkedProductCustomizable,
  isModelImageFullScreen,
  isPreorderable,
  isProductCustomizable,
  isProductSet,
  product: _product,
  shouldShowConfidenceAssets,
  showToastMessage,
  trackViewedPdpImage,
  trackPDPLearnMoreEvent,
}) {
  // TODO: "one size" items don't get properly labeled as missy/curvy in
  // product_id_object_list on the backend so we have to do it here. This
  // should be handled by API or ES (please delete this hook when TRO/ES
  // work is done).
  const {
    'customer-also-viewed': customerAlsoViewedFeature,
    'new-product-pricing': shouldDisplayNewProductPricing,
    'experiment-new-pricing-promo-banners': newPricingPromoBannersFlag,
  } = useLDFlags();

  const pdpSingleCTAFlag = usePdpSingleCTAFlag();

  const { isServerOnly } = useSession();

  const displayNewProductPricing = isNewPricingPromoBannersFlagOn(
    newPricingPromoBannersFlag
  )
    ? newPricingPromoBannersFlag.newPricing
    : shouldDisplayNewProductPricing;

  const [savageXYouFeature] = useFeatures(['savage_x_you']);

  const product = useReorderOneSizeLinkedProducts(_product, isCurvy);
  const [, setViewedPDPImages] = useLocalStorage('viewedPDPImages', {
    enableSessionStorage: true,
    defaultValue: defaultImages,
  });

  const trackViewedPdpImageWithSessionStorage = useCallback(
    (imageUrl, onAdd) => {
      setViewedPDPImages(prevImages => {
        let nextImages;

        if (Array.isArray(prevImages)) {
          nextImages = [...prevImages];
        } else {
          nextImages = defaultImages;
        }

        if (!nextImages.includes(imageUrl)) {
          nextImages.push(imageUrl);
          onAdd();
        }

        return nextImages;
      });
    },
    [setViewedPDPImages]
  );

  const {
    orderedModels,
    initialModel,
    isMatchUserSizeFeatureEnabled,
    initialModelId,
    closestModelBySize,
  } = useOrderedModels(_product, isCurvy, isProductSet);

  const [selectedModelId, setSelectedModelId] = useState(initialModelId);
  const { isDesktop, isMobile } = useBreakpoint();

  // if we have ambassdor model, ambassdor will be the first model and should be selected by default
  const [isAmbassadorModelSelected, setIsAmbassadorModelSelected] = useState(
    initialModel && initialModel.isCombinedModel
  );
  const { isFlowInitiated: shouldHideRecommendations } = useByoContext();

  // This ensures we're calling `getMembershipPeriod` and other functions related
  // to Unlocked Xclusives.
  useUnlockedXclusive();

  const stickyHeaderRef = useRef();
  const stickyContentRef = useRef();
  const imageCarouselRef = useRef();
  const customToggleRef = useRef();
  const [setDetailsNode, setSetDetailsNode] = useState(null);

  const [showVipModal, setShowVipModal] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [hideRegCta, setHideRegCta] = useState(false);
  const [topItemHeaderPosition, setTopItemHeaderPosition] = useState(0);
  const [customText, setCustomText] = useState('');
  const [customTextError, setCustomTextError] = useState('');

  const isSavageXYouFeatureEnabled = savageXYouFeature?.data?.value;

  const { isInfluencer } = useCustomer();
  const { isVip, networkStatus, isDowngraded } = useMembership();
  const isShopWithConfidenceEnabled = !(isVip || isDowngraded);

  const priceDetails = useSelector(state =>
    getPriceDetails(state, { product })
  );

  const { isXtraVipBox, isSoldOut } = useSelector(state => {
    return {
      isXtraVipBox: isXtraVipBoxSelector(state, { product }),
      isSoldOut: isSoldOutSelector(state, { product }),
    };
  });
  const membershipResolved = !isServerOnly && networkStatus.isUpToDate;
  const showPriceSelectors = useMemo(() => {
    return (
      pdpSingleCTAFlag.enabled &&
      !isVip &&
      (hideRegCta ||
        (membershipResolved &&
          !isInfluencer &&
          !hasMembershipItem &&
          ((isProductSet && !isXtraVipBox) || !isProductSet) &&
          !isSoldOut))
    );
  }, [
    pdpSingleCTAFlag,
    hideRegCta,
    membershipResolved,
    isVip,
    isInfluencer,
    hasMembershipItem,
    isProductSet,
    isXtraVipBox,
    isSoldOut,
  ]);

  const [priceOptionSelected, setPriceOptionSelected] = useState(
    pdpSingleCTAFlag.value
  );
  const [priceSelectorErrorCounter, setPriceSelectorErrorCounter] = useState(0);

  // TaggStar element detection flag.
  const [hasTaggStarElement, setHasTaggStarElement] = useState(false);
  const carouselRef = useRef(null);

  // Use a MutationObserver to detect if a TaggStar element is present
  // inside the carousel, so we can apply custom styling to it.
  useEffect(() => {
    if (!carouselRef.current) {
      return;
    }

    const observer = new MutationObserver(() => {
      const taggStarElement =
        carouselRef.current.querySelector('.tagg-product-page');
      setHasTaggStarElement(!!taggStarElement);
    });

    observer.observe(carouselRef.current, {
      childList: true,
      subtree: true,
    });

    // Set the initial state of the TaggStar detection flag.
    setHasTaggStarElement(
      !!carouselRef.current.querySelector('.tagg-product-page')
    );

    return () => observer.disconnect();
  }, []);

  const incrementPriceSelectorError = useCallback(() => {
    setPriceSelectorErrorCounter(priceSelectorErrorCounter + 1);
  }, [priceSelectorErrorCounter]);

  const productDetailContext = useMemo(
    () => ({
      singleCTA: {
        enabled: pdpSingleCTAFlag.enabled && !isVip,
        showPriceSelectors,
        showPriceOnCTAs: !isProductSet,
        priceOptionSelected: priceOptionSelected || pdpSingleCTAFlag.value,
        priceSelectorErrorCounter,
        incrementPriceSelectorError,
        setPriceOptionSelected,
      },
    }),
    [
      pdpSingleCTAFlag,
      isVip,
      showPriceSelectors,
      isProductSet,
      priceOptionSelected,
      priceSelectorErrorCounter,
      incrementPriceSelectorError,
      setPriceOptionSelected,
    ]
  );

  // If 'Shop With Confidence' is enabled, 'Shipping Exchange' should NOT show.
  const shouldShowShippingExchange = !(
    isShopWithConfidenceEnabled ||
    (!isProductSet && shouldShowConfidenceAssets)
  );

  const { tld } = useDomain();
  const shouldProductFitReviewAlignToRight = ['.de', '.fr', '.es'].includes(
    tld
  );

  const selectedModel = useMemo(
    () =>
      orderedModels.find(model => model.modelId === selectedModelId) ||
      initialModel,
    [orderedModels, initialModel, selectedModelId]
  );

  /*
   * There are instances that when selecting a size, the user will be redirected
   * to its related product. Thus, we also select the model from the product
   * with the closest size to the new size. This prevents showing of LAYDOWN only.
   */
  const handleSizeChange = useCallback(
    (productId, newSize) => {
      if (isMatchUserSizeFeatureEnabled) {
        const closestModelBySizeId = closestModelBySize(
          orderedModels.filter(
            model => model.product.master_product_id === productId
          ),
          newSize?.value
        )?.modelId;

        if (closestModelBySizeId) {
          setSelectedModelId(closestModelBySizeId);
        }
      } else {
        setSelectedModelId(
          (
            orderedModels.find(
              model => model.product.master_product_id === productId
            ) || selectedModel
          ).modelId
        );
      }
    },
    [
      closestModelBySize,
      isMatchUserSizeFeatureEnabled,
      orderedModels,
      selectedModel,
    ]
  );

  const closeVipModal = useCallback(() => {
    setShowVipModal(false);
  }, []);

  const handleVipClick = useCallback(() => {
    setShowVipModal(prevShowVipModal => !prevShowVipModal);
  }, []);

  const handleHideRegCta = useCallback(option => {
    setHideRegCta(option);
  }, []);

  const handleTopItemHeaderPosition = useCallback(() => {
    if (!stickyHeaderRef.current) {
      return 0;
    }
    const topPosition =
      stickyHeaderRef.current.state.height + stickyHeaderRef.current.state.top;
    setTopItemHeaderPosition(topPosition);
  }, [stickyHeaderRef]);

  const updateSticky = () => {
    const content = stickyContentRef.current;
    if (!content) {
      return;
    }

    // Will need to manually update the sticky to not be on top of the footer
    content.updateInitialDimension();
    content.update();
  };

  useEffect(() => {
    // Create a ResizeObserver on the #setDetails dom element
    // When the collapsable panel is collapsed or closed update the stickyContent
    if (setDetailsNode == null) {
      return;
    }

    const setDetailObserver = new ResizeObserver(entries => {
      // Ignore other elements we only need to update setDetailsNode
      const setDetail = entries.find(({ target }) => target === setDetailsNode);
      if (!setDetail) {
        return;
      }

      updateSticky();
    });

    // Observer setDetailsNode for resize changes from collapsing
    setDetailObserver.observe(setDetailsNode);

    // On unmount disconnect the observer
    return () => setDetailObserver.disconnect();
  }, [setDetailsNode]);

  useEffect(() => {
    if (showVipModal) {
      trackPDPLearnMoreEvent();
    }
  }, [showVipModal, trackPDPLearnMoreEvent]);

  const handleSwipe = useCallback(
    async i => {
      let nextActiveSlide;
      const thumbCount = selectedModel.angles.length - 1;
      if (activeSlide >= 0 && activeSlide < thumbCount && i === 'left') {
        nextActiveSlide = activeSlide + 1;
      } else if (
        activeSlide >= 1 &&
        activeSlide <= thumbCount &&
        i === 'right'
      ) {
        nextActiveSlide = activeSlide - 1;
      }

      if (
        Number.isInteger(nextActiveSlide) &&
        nextActiveSlide !== activeSlide
      ) {
        let angle = nextActiveSlide + 1;
        let imageUrl;

        if (
          selectedModel.angles[nextActiveSlide] === 'laydown' ||
          selectedModel.angles[nextActiveSlide] === 'video'
        ) {
          angle = selectedModel.angles[nextActiveSlide];
        }

        const { vimeo_id: vimeoId } = selectedModel.product;
        if (angle === 'video' && vimeoId) {
          try {
            const videoUrl = Video.getUrlFromVideoId(vimeoId);
            const data = await fetchVideoThumbnail(videoUrl);

            if (data.thumbnail_url) {
              imageUrl = formatVimeoThumbnailUrl(
                data.thumbnail_url,
                VideoCoverSizes.SMALL
              );
            }
          } catch (err) {
            Sentry.captureException(err);
          }
        } else {
          imageUrl = generateCdnImageUrl({
            product,
            angle,
          });
        }

        const {
          master_product_id: _masterProductId = -1,
          product_id_object_list: productIdObjectList,
        } = product;

        const masterProductId = _masterProductId.toString();

        trackViewedPdpImageWithSessionStorage(imageUrl, () => {
          const modelDataInfo = prepModelData(selectedModel);
          const productDetail = productIdObjectList?.find(
            item => item.size === modelDataInfo.model_size
          );
          trackViewedPdpImage({
            label: 'swipe',
            location: 'carousel',
            position: nextActiveSlide + 1,
            bundle_product_id_hit: isProductSet ? masterProductId : null,
            image_url: imageUrl,
            is_plus_size: productDetail?.is_plus_size || isCurvy,
            product_id_hit: masterProductId,
            ...modelDataInfo,
          });
        });

        setActiveSlide(nextActiveSlide);
      }
    },
    [
      activeSlide,
      isCurvy,
      isProductSet,
      product,
      selectedModel,
      trackViewedPdpImage,
      trackViewedPdpImageWithSessionStorage,
    ]
  );

  const handlePlayButton = useCallback(
    async event => {
      const indexVideo = selectedModel.angles.length - 2;

      // The video needs to a least to added to the list angles
      if (event && selectedModel.angles[indexVideo] === 'video') {
        let imageUrl = null;

        const { vimeo_id: vimeoId } = selectedModel.product;
        if (vimeoId) {
          try {
            const videoUrl = Video.getUrlFromVideoId(vimeoId);
            const data = await fetchVideoThumbnail(videoUrl);

            if (data.thumbnail_url) {
              imageUrl = formatVimeoThumbnailUrl(
                data.thumbnail_url,
                VideoCoverSizes.SMALL
              );
            }
          } catch (err) {
            Sentry.captureException(err);
          }
        }

        const {
          // eslint-disable-next-line camelcase
          master_product_id = -1,
          product_id_object_list: productIdObjectList,
        } = product;

        const masterProductId = master_product_id.toString();

        trackViewedPdpImageWithSessionStorage(imageUrl, () => {
          const modelDataInfo = prepModelData(selectedModel);
          const productDetail = productIdObjectList?.find(
            item => item.size === modelDataInfo.model_size
          );

          trackViewedPdpImage({
            label: 'click',
            location: 'carousel',
            position: selectedModel.angles.length - 1,
            bundle_product_id_hit: isProductSet ? masterProductId : null,
            image_url: imageUrl,
            is_plus_size: productDetail?.is_plus_size || isCurvy,
            product_id_hit: masterProductId,
            ...modelDataInfo,
          });
        });
      }
      imageCarouselRef.current.slickGoTo(indexVideo, true);
    },
    [
      isCurvy,
      isProductSet,
      product,
      selectedModel,
      trackViewedPdpImage,
      trackViewedPdpImageWithSessionStorage,
    ]
  );

  const handleCustomTextChange = useCallback(event => {
    setCustomText(event.target.value);
    if (!event.target.value) {
      setCustomTextError('ERROR_EMPTY_VALUE');
    } else if (event.target.value.length > 10) {
      setCustomTextError('ERROR_INVALID_LENGTH');
    } else if (!/^[A-Za-z@:#!.,$?"'` ]+$/.test(event.target.value)) {
      setCustomTextError('ERROR_FORBIDDEN_CHARACTER');
    } else {
      setCustomTextError('');
    }
  }, []);

  const renderAddToBagError = useCallback(
    errorCode => {
      switch (errorCode) {
        case AddToBagErrorCode.OUT_OF_STOCK:
          showToastMessage(
            <FormattedMessage
              id="global_checkout.out_of_stock_error_message"
              defaultMessage="Sorry, this item is out of stock."
            />
          );
          break;
        case AddToBagErrorCode.INAPPROPRIATE_CUSTOM_TEXT:
          showToastMessage(
            <FormattedMessage
              id="global_checkout.inappropriate_custom_text_error_message"
              defaultMessage="We see you getting creative, but let’s try something different."
            />
          );
          break;
        default:
          showToastMessage(
            <FormattedMessage
              id="global_checkout.general_error"
              defaultMessage="Oops we’ve encountered an error. Try again"
            />
          );
          break;
      }
    },
    [showToastMessage]
  );

  const renderProductSetDetails = () => {
    const itemCount = product.products_in_set.length;

    return (
      <SetWrapper>
        <ProductSetDetailSizeLinks product={product} isProductSet />
        <ProductDetailSizeIncludeLabel>
          <FormattedMessage
            id="site_pdp.this_set_includes"
            defaultMessage="This {itemCount, number}-Piece Set Includes"
            values={{ itemCount }}
          />
        </ProductDetailSizeIncludeLabel>
        {(isProductCustomizable || isLinkedProductCustomizable) &&
        isSavageXYouFeatureEnabled ? (
          <CustomizationToggleWrapper ref={customToggleRef}>
            <ProductDetailCustomizationToggle
              customText={customText}
              customTextError={customTextError}
              onCustomTextChange={handleCustomTextChange}
              product={product}
            />
          </CustomizationToggleWrapper>
        ) : null}
        <ProductSetDetailWrapper
          id="setDetails"
          ref={setSetDetailsNode}
          data-autotag="pdpSetsSizeSection"
        >
          <ProductSetDetailOptions
            customToggleRef={customToggleRef}
            customText={customText}
            customTextError={customTextError}
            shouldCheckCustomTextErr={
              isProductCustomizable && isSavageXYouFeatureEnabled
            }
            showToastMessage={showToastMessage}
            renderAddToBagError={renderAddToBagError}
            product={product}
            isProductSet
            handleHideRegCta={handleHideRegCta}
            hideRegCta={hideRegCta}
            topItemHeaderPosition={topItemHeaderPosition}
            onSetHeaderPositionchange={handleTopItemHeaderPosition}
            pSource="set_component"
          />
        </ProductSetDetailWrapper>
      </SetWrapper>
    );
  };

  const renderSingleProductDetails = () => {
    /* The "Size Guide" & "Additional Sizes Available" links need to appear in between the size selector and CTA */
    return (
      <ProductSingleDetailOptions
        showToastMessage={showToastMessage}
        renderAddToBagError={renderAddToBagError}
        product={product}
        handleHideRegCta={handleHideRegCta}
        hideRegCta={hideRegCta}
        key={product.default_product_category_id}
        shouldShowConfidenceAssets={shouldShowConfidenceAssets}
        shouldProductFitReviewAlignToRight={shouldProductFitReviewAlignToRight}
        handleSizeChange={handleSizeChange}
      />
    );
  };

  const renderLeftColumnLayout = () => {
    const getLayout = (StickyComponent, bottomBoundary) => {
      return isDesktop ? (
        <StickyComponent
          top="#navbar"
          bottomBoundary={bottomBoundary}
          ref={stickyContentRef}
          enabled={!isModelImageFullScreen}
        >
          {renderLeftColumn()}
        </StickyComponent>
      ) : (
        renderLeftColumn()
      );
    };

    switch (true) {
      case isProductSet:
        return getLayout(StyledSticky, '#setDetails');
      case shouldShowConfidenceAssets:
        return getLayout(StickyLeftColumnContent, '#rightColumn');

      default:
        return renderLeftColumn();
    }
  };

  const renderLeftColumn = () => {
    const shouldShowModelThumbnails = !!(orderedModels && orderedModels.length);

    const { vimeo_id: vimeoId } = selectedModel.product;
    // bootstrap way to tell whether it is default model
    if (orderedModels.indexOf(selectedModel) === 0) {
      selectedModel.isDefaultModel = true;
    } else {
      selectedModel.isMemberModel = true;
    }

    return (
      <>
        {isMobile ? (
          <MobileWrapper>
            <MobileHeaderWrapper>
              <ProductDetailName product={product} />
              <StyledWishlistButton
                product={product}
                eventCategory="PDP"
                overlay
                large
              />
            </MobileHeaderWrapper>

            {!isPreorderable && !isProductSet && (
              <StyledProductDetailStars
                product={product}
                scrollTo="#reviews"
                showReviewCount
                showAverageRating
                starSize={12}
              />
            )}
            <StyledProductDetailFlag product={selectedModel.product} />
          </MobileWrapper>
        ) : null}
        <CarouselWrapper ref={carouselRef}>
          {isMobile && (
            <WrapperButtons>
              {vimeoId && (
                <ProductDetailPlayButton onClick={handlePlayButton}>
                  Watch Video
                  <GrPlay />
                </ProductDetailPlayButton>
              )}
              <ExpandIcon onClick={() => handleModelImageFullScreen(true)}>
                <BsArrowsAngleExpand size={18} />
              </ExpandIcon>
            </WrapperButtons>
          )}
          <StyledProductImageCarousel
            flag={<StyledProductDetailFlag product={selectedModel.product} />}
            handleModelImageFullScreen={handleModelImageFullScreen}
            imageCarouselRef={imageCarouselRef}
            isAmbassadorModelSelected={isAmbassadorModelSelected}
            isCurvy={isCurvy}
            isExclusive={isExclusive}
            isModelImageFullScreen={isModelImageFullScreen}
            isProductSet={isProductSet}
            model={selectedModel}
            onAmbassadorModelSelect={setIsAmbassadorModelSelected}
            options={{ onSwipe: i => handleSwipe(i) }}
            product={product}
            shouldShowModelThumbnails={shouldShowModelThumbnails}
            trackViewedPdpImageWithSessionStorage={
              trackViewedPdpImageWithSessionStorage
            }
            hasTaggStarElement={hasTaggStarElement}
          />
        </CarouselWrapper>
        {shouldShowModelThumbnails && isModelEnabled && !isProductSet ? (
          <ProductDetailModelThumbnails
            isAmbassadorModelSelected={isAmbassadorModelSelected}
            isCurvy={isCurvy}
            isProductSet={isProductSet}
            models={orderedModels}
            onAmbassadorModelSelect={setIsAmbassadorModelSelected}
            onChangeModel={setSelectedModelId}
            selectedModel={selectedModel}
            trackViewedPdpImageWithSessionStorage={
              trackViewedPdpImageWithSessionStorage
            }
          />
        ) : null}
        {isDesktop ? (
          <ProductDetailDescriptionWrapper>
            <ProductDetailDescription
              product={product}
              isProductSet={isProductSet}
              shouldShowShippingExchange={shouldShowShippingExchange}
            />
          </ProductDetailDescriptionWrapper>
        ) : null}
      </>
    );
  };

  const renderSetRightColumn = () => {
    return (
      <>
        <Details>
          {isDesktop && pdpSingleCTAFlag.enabled ? (
            <StickyHeaderSet
              onStateChange={handleTopItemHeaderPosition}
              top="#navbar"
              bottomBoundary="#setDetails"
              ref={stickyHeaderRef}
              enabled={!isModelImageFullScreen}
            >
              <ProductDetailHeader
                product={product}
                isProductSet={isProductSet}
              />
            </StickyHeaderSet>
          ) : (
            <ProductDetailHeader
              product={product}
              isProductSet={isProductSet}
            />
          )}
          {renderProductSetDetails()}
          {!pdpSingleCTAFlag.enabled && hasMembershipItem && (
            <VipLearnMoreLink onClick={handleVipClick} />
          )}
        </Details>
        {isDesktop ? (
          <ProductDetailRecommendations product={product} />
        ) : (
          <ProductDetailDescriptionWrapper>
            <ProductDetailDescription product={product} isProductSet />
          </ProductDetailDescriptionWrapper>
        )}
      </>
    );
  };

  const renderSingleRightColumn = () => {
    return (
      <>
        <Details>
          <ProductDetailHeader
            product={product}
            isProductSet={isProductSet}
            shouldDisplayNewProductPricing={displayNewProductPricing}
          />
          {renderSingleProductDetails()}
          {!pdpSingleCTAFlag.enabled && hasMembershipItem && (
            <VipLearnMoreLink onClick={handleVipClick} />
          )}
          {!shouldShowShippingExchange ? (
            <ProductDetailConfidence isPreorderable={isPreorderable} />
          ) : null}
        </Details>
        {isMobile ? (
          <ProductDetailDescriptionWrapper>
            <ProductDetailDescription
              product={product}
              shouldShowShippingExchange={shouldShowShippingExchange}
            />
          </ProductDetailDescriptionWrapper>
        ) : null}
      </>
    );
  };

  return (
    <ProductDetailContext.Provider value={productDetailContext}>
      <Row
        className={className}
        data-cnstrc-product-detail
        data-cnstrc-item-id={product.master_product_id}
        data-cnstrc-item-name={product.label}
        data-cnstrc-item-price={priceDetails.displayPrice}
      >
        <RightColumn id="rightColumn">
          {isProductSet ? renderSetRightColumn() : renderSingleRightColumn()}
        </RightColumn>
        <LeftColumn>{renderLeftColumnLayout()}</LeftColumn>
      </Row>
      {shouldHideRecommendations ? null : (
        <RecommendedContainer>
          <SuggestedProductContainer hasDarkBackground>
            <StyledPairItWithRecommendations
              source="pair_it_with"
              pSource="pair_it_with"
              shouldUsePairItWith
              product={product}
              hidePrice={false}
              hidePromo={false}
              options={{
                arrows: true,
                dots: true,
                slidesToShow: 2,
                slidesToScroll: 2,
                responsive: [
                  {
                    // Large Tablet, Small Desktop
                    breakpoint: 1023,
                    settings: {
                      arrows: true,
                      slidesToShow: 2,
                      slidesToScroll: 2,
                    },
                  },
                  {
                    breakpoint: 767,
                    settings: {
                      arrows: false,
                      slidesToShow: 1,
                      slidesToScroll: 1,
                      swipeToSlide: true,
                    },
                  },
                ],
              }}
              title={
                <FormattedMessage
                  id="site_pdp.pair_it_with_title"
                  defaultMessage="Pair It With"
                />
              }
              variantStyle
              trackingSource="YMAL"
            />
          </SuggestedProductContainer>
          {customerAlsoViewedFeature ? (
            <SuggestedProductContainer hasTopBorder>
              <StyledProductDetailRecommendations
                source="recommended"
                pSource="customer_also_viewed"
                product={product}
                maxProducts={10}
                hidePrice={false}
                options={{
                  arrows: true,
                  dots: false,
                  slidesToShow: 5,
                  slidesToScroll: 5,
                  responsive: [
                    {
                      // Large Tablet, Small Desktop
                      breakpoint: 1023,
                      settings: {
                        arrows: true,
                        slidesToShow: 3,
                        slidesToScroll: 3,
                      },
                    },
                    {
                      breakpoint: 767,
                      settings: {
                        arrows: false,
                        slidesToShow: 2,
                        slidesToScroll: 1,
                        swipeToSlide: true,
                      },
                    },
                  ],
                }}
                title={
                  <FormattedMessage
                    id="site_pdp.customer_also_viewed_title"
                    defaultMessage="Customer Also Viewed"
                  />
                }
                variantStyle
                trackingSource="CAV"
                shouldUseBmigComponents
              />
            </SuggestedProductContainer>
          ) : null}
        </RecommendedContainer>
      )}

      {!isPreorderable && !isProductSet && (
        <Reviews id="reviews" data-autotag="desktop-reviews">
          <StyledProductDetailReviews
            location="desktop"
            product={product}
            key={product.master_product_id}
          />
        </Reviews>
      )}
      <VIPLearnMoreModal blur isOpen={showVipModal} onExit={closeVipModal} />
      <ProductImpression product={product} />
    </ProductDetailContext.Provider>
  );
}

ProductDetailLayout.propTypes = {
  className: PropTypes.string,
  handleModelImageFullScreen: PropTypes.func,
  hasMembershipItem: PropTypes.bool,
  isCurvy: PropTypes.bool,
  isExclusive: PropTypes.bool,
  isLinkedProductCustomizable: PropTypes.bool,
  isModelImageFullScreen: PropTypes.bool,
  isPreorderable: PropTypes.bool,
  isProductCustomizable: PropTypes.bool,
  isProductSet: PropTypes.bool,
  product: PropTypes.object.isRequired,
  shouldShowConfidenceAssets: PropTypes.bool,
  showToastMessage: PropTypes.func,
  trackViewedPdpImage: PropTypes.func,
  trackPDPLearnMoreEvent: PropTypes.func,
};

ProductDetailLayout.defaultProps = {
  isExclusive: false,
  isProductSet: false,
};
